import React, { memo, useEffect, useState, type ReactNode } from 'react';
import { useSsrSafeCallback } from '@jira/ssr__ssr-toolkit/src/hooks/useSsrSafeCallback.tsx';
import { getViewModeFromLocalStorage } from '@jira/issue__issue-preview-view-mode-storage/src/getViewModeFromLocalStorage.tsx';
import {
	IssuePreviewViewModeContainer,
	type HydratedViewMode,
} from './issuePreviewViewModeStore.tsx';

type Props = {
	children: ReactNode;
};

const getViewModeWithSidebarDefault = (viewMode: HydratedViewMode): HydratedViewMode => {
	if (viewMode === null) {
		return null;
	}
	return viewMode === 'DEFAULT' ? 'SIDEBAR' : viewMode;
};

const getInitialViewMode = (): HydratedViewMode =>
	getViewModeWithSidebarDefault(getViewModeFromLocalStorage());

const IssuePreviewViewModeProvider: React.NamedExoticComponent<Props> = memo(
	({ children }: Props) => {
		const ssrSafeCall = useSsrSafeCallback();

		const [viewMode, setViewMode] = useState<HydratedViewMode>(() =>
			ssrSafeCall(getInitialViewMode, null),
		);

		useEffect(() => {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state
			setViewMode(getInitialViewMode());
		}, []);

		return (
			<IssuePreviewViewModeContainer initialViewMode={viewMode}>
				{children}
			</IssuePreviewViewModeContainer>
		);
	},
);

IssuePreviewViewModeProvider.displayName = 'IssuePreviewViewModeProvider';

export default IssuePreviewViewModeProvider;
